import { api } from "./client";
import {
  MIN_MACHINE_VERSION_DEFAULT_KEY,
  canonicalRemoteKey,
  meetsMinimum,
  parseMachineVersion,
} from "@mycompbox/shared";
import type {
  JobType,
  JobStatus,
  TurnRole,
  JobWaitingReason,
  NeedsInputRequest,
} from "@mycompbox/shared";

export type { JobType, JobStatus, TurnRole, JobWaitingReason, NeedsInputRequest };

/**
 * Mailbox API client (ENG-E23 / ENG-123 browser flow).
 *
 * The browser drops jobs into a paired machine's mailbox and reads the
 * interview turns + execution progress back. Endpoints (all under /api/v1):
 *   GET  /machines        — list the caller's paired machines
 *   POST /jobs            — enqueue a job for a machine
 *   GET  /jobs/:id        — job status + turns + linked progress
 *   POST /jobs/:id/turns  — append a user turn (answer the interview)
 *
 * Dates are ISO strings (JSON-serialized), matching the rest of the web API
 * layer. Enum-like fields reuse the shared string unions so the browser and
 * API agree on one source of truth.
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Server-derived readiness of a machine's agent runtime (ENG-138/ENG-139).
 * This mirrors the API's `MachineReadiness` enum exactly — the server is the
 * authoritative source (see `deriveMachineReadiness` in machineService.ts).
 *  - `ready`             — recently seen AND its agent runtime reported `ok`.
 *  - `runtime-not-found` — recently seen but the runtime is not `ok`
 *                          (missing/errored/unknown — e.g. an OLD daemon that
 *                          predates runtime-reporting reads as this too).
 *  - `offline`           — not seen within the online window (daemon is down).
 *
 * The web layers one more state on top (ENG-322), never sent by the server:
 *  - `outdated`          — online, but its CLI is below the minimum version the
 *                          API reports for the job kind (see
 *                          {@link machineVersionState}). Not dispatchable until
 *                          the user runs `compbox update`.
 */
export type ServerMachineReadiness = "ready" | "runtime-not-found" | "offline";
export type MachineReadiness = ServerMachineReadiness | "outdated";

/**
 * How a machine's CLI version compares with the minimum (ENG-322):
 *  - `current`  — at or above the minimum.
 *  - `outdated` — below the minimum; the server will refuse to give it work.
 *  - `unknown`  — no (valid) version reported yet; non-blocking, the server
 *                 keeps such machines eligible.
 */
export type MachineVersionState = "current" | "outdated" | "unknown";

/** A paired machine (device) as returned by GET /machines. */
export interface Machine {
  id: string;
  name: string;
  /** 'active' | 'inactive' | 'revoked' */
  status: string;
  /** ISO timestamp of the daemon's last check-in, or null if never seen. */
  lastSeenAt: string | null;
  /**
   * Last agent-runtime status reported by the daemon (ENG-138):
   * 'ok' | 'not-found' | 'error' | 'unknown', or null if never reported.
   */
  agentRuntimeStatus: "ok" | "not-found" | "error" | "unknown" | null;
  /** Free-text detail accompanying the last runtime report (ENG-138). */
  agentRuntimeDetail: string | null;
  /** ISO timestamp of when the daemon last checked its runtime (ENG-138). */
  agentRuntimeCheckedAt: string | null;
  /**
   * Server-derived, authoritative readiness (ENG-138). Prefer this over
   * recomputing recency client-side; see {@link machineReadiness}.
   */
  readiness: ServerMachineReadiness;
  /**
   * Last CLI semver the daemon reported via X-CompBox-Version (ENG-318), or
   * null if it never has (older CLI). Absent on APIs predating ENG-318.
   */
  cliVersion?: string | null;
  /** ISO timestamp of when cliVersion was last recorded (ENG-318). */
  lastVersionAt?: string | null;
  /**
   * Effective minimum CLI version per job kind plus a `default` entry, as
   * resolved by the server from MIN_MACHINE_VERSION (ENG-322). The web never
   * hardcodes a minimum; when this is absent (older API) nothing is outdated.
   */
  minMachineVersion?: Record<string, string>;
  /**
   * Canonical git remotes this machine's daemon can service (ENG-176/ENG-178),
   * each already normalized server-side via `normalizeGitRemote`. The dispatch
   * dialog compares the target project's repo against this list to decide which
   * machines can run it (see {@link machineServicesRemote}).
   *
   * A payload from an API old enough to predate ENG-176 omits this field; the
   * value is then `undefined` at runtime and MUST be treated as `[]` (no repo
   * serviced) rather than dereferenced — see the `?? []` guards at every read.
   */
  servicedRemotes?: string[];
  /**
   * Whether usage-limited orchestrate runs on this machine auto-continue once
   * the limit resets (ENG-334). Server default is `true`; an API that predates
   * ENG-334 omits the field, so treat `undefined` as `true`.
   */
  autoContinue?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * An attachment (image or document) linked to a job/turn (ENG-150/ENG-151 read-contract).
 *
 * `signedUrl` is a short-TTL URL into the private bucket and can be used
 * directly as an `<img>` `src` (images) or link `href` (documents). The API is
 * authoritative on size/MIME (2 MB + image/document allowlist, enforced
 * server-side); the client only mirrors those limits for
 * fast inline UX feedback before an upload is attempted.
 */
export interface JobAttachment {
  id: string;
  mimeType: string;
  sizeBytes: number;
  signedUrl: string;
}

/** A single turn in a job's interview/conversation log. */
export interface JobTurn {
  id: string;
  role: TurnRole;
  content: string;
  createdAt: string;
  /**
   * Images linked to this turn (ENG-151 read-contract). Always present as an
   * array on turns from an attachment-aware API (`[]` when none); optional here
   * so turns from an older API shape still type-check.
   */
  attachments?: JobAttachment[];
}

/** Lightweight execution progress for an epic linked to a job. */
export interface EpicProgress {
  epicId: string;
  name: string;
  totalFeatures: number;
  completedFeatures: number;
  totalTasks: number;
  completedTasks: number;
}

/** A job with its turns and any linked epic progress (GET /jobs/:id). */
export interface Job {
  id: string;
  machineId: string;
  /** "cloud": runs on CompBox's server with the person's API key, driven by the browser. */
  runsOn: "computer" | "cloud";
  organizationId: string;
  type: JobType;
  status: JobStatus;
  payload: unknown;
  result: unknown;
  /** When a "waiting" job may run again (ENG-333), or null. */
  resumeAt?: string | null;
  /**
   * Why a "waiting" job is parked (ENG-338): "needs_input" while it waits for
   * the owner's reply, "reply_received" once answered; null for a usage-limit wait.
   */
  waitingReason?: JobWaitingReason | null;
  /** The request a "needs_input" job waits on (ENG-338), or null. */
  needsInput?: NeedsInputRequest | null;
  /** The job this one continues (ENG-334), or null for an original run. */
  continuedFromJobId?: string | null;
  /** Position in the continuation chain; 0 for the original run. */
  continuationIndex?: number;
  /** The continuation that picked up after this job, or null (ENG-335). */
  nextContinuationJobId?: string | null;
  /** Most continuations one run may chain. */
  maxContinuations?: number;
  createdAt: string;
  updatedAt: string;
  turns: JobTurn[];
  progress: EpicProgress | null;
  /** ENG-347: pull requests merged through CompBox for this run, newest first. */
  mergeRecords?: JobMergeRecord[];
  /** ER-130: when the job's computer last called in (a dead runner shows as offline). */
  machineLastSeenAt?: string | null;
}

/** ER-130: what "All merged — continue" / "Restart" did. */
export interface ContinueRunResult {
  /** "resumed": the same run continues; "restarted": a new run was queued (jobId). */
  mode: "resumed" | "restarted";
  jobId: string;
  waitingForDeploy: boolean;
  merged: { number: number; title: string | null; baseRef: string | null; mergedSha: string | null }[];
  deferred: { id: string; identifier: string; title: string }[];
}

/** One merge through CompBox, as listed on the run page (ENG-347). */
export interface JobMergeRecord {
  id: string;
  prNumber: number;
  repoFullName: string;
  prUrl: string;
  /** "manual" (a person merged), "ask" (the owner approved), "auto" (the project's policy). */
  policy: string;
  /** The person's name, or "policy:auto". */
  approverLabel: string;
  mergedSha: string | null;
  createdAt: string;
}

export interface EnqueueJobInput {
  machineId: string;
  type: JobType;
  /** Arbitrary request payload; JSON-serialized server-side. */
  payload: unknown;
}

export interface PostTurnInput {
  content: string;
  /**
   * IDs of attachments (already uploaded via {@link mailboxApi.uploadAttachment}
   * or {@link mailboxApi.uploadAttachmentUrl}) to link to the turn being sent.
   */
  attachmentIds?: string[];
}

// =============================================================================
// Online / offline helper
// =============================================================================

/**
 * A machine is considered offline if it has never checked in, or its last
 * heartbeat is older than this threshold. The daemon polls (heartbeats) well
 * within this window, so exceeding it means the CompBox session isn't running.
 *
 * MIRRORS the server's `MACHINE_ONLINE_THRESHOLD_MS` (machineService.ts) —
 * keep the two in sync. Sized at 2.5x the daemon's slowest heartbeat cadence
 * (60s while a planner child runs) so an actively-working machine never flaps
 * offline between keep-alives.
 */
export const MACHINE_ONLINE_THRESHOLD_MS = 150_000;

/** True when the machine is `active` and has heartbeated recently. */
export function isMachineOnline(
  machine: Pick<Machine, "status" | "lastSeenAt">,
  now: number = Date.now()
): boolean {
  if (machine.status !== "active") return false;
  if (!machine.lastSeenAt) return false;
  const seen = new Date(machine.lastSeenAt).getTime();
  if (Number.isNaN(seen)) return false;
  return now - seen <= MACHINE_ONLINE_THRESHOLD_MS;
}

/**
 * Authoritative readiness for a machine, as used by the launcher dialog and
 * readiness indicators.
 *
 * The server derives `readiness` (ENG-138) using both a recent heartbeat AND
 * the reported agent-runtime status; it is the source of truth, so we return
 * it verbatim when present. The client-side fallback below only kicks in if the
 * field is absent (e.g. a response from an API old enough to predate ENG-138) —
 * it can only distinguish online vs offline, never `runtime-not-found`, since
 * that distinction requires the runtime status the old API didn't report.
 */
export function machineReadiness(
  machine: Pick<
    Machine,
    "status" | "lastSeenAt" | "agentRuntimeStatus" | "readiness"
  > &
    Partial<Pick<Machine, "cliVersion" | "minMachineVersion">>,
  now: number = Date.now(),
  jobKind: string = MIN_MACHINE_VERSION_DEFAULT_KEY
): MachineReadiness {
  // Resilience fallback when the server field is missing: we cannot infer
  // runtime-not-found without a runtime status, so collapse to online/offline.
  // (`readiness` is typed required, but older APIs omit it — hence the guard.)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const base: ServerMachineReadiness = machine.readiness
    ? machine.readiness
    : isMachineOnline(machine, now)
      ? "ready"
      : "offline";
  // An offline machine reads as offline whatever its version; otherwise an
  // outdated CLI takes precedence because the server will refuse it work.
  if (base === "offline") return base;
  return machineVersionState(machine, jobKind) === "outdated" ? "outdated" : base;
}

/**
 * Compares a machine's reported CLI version with the API-provided minimum for
 * `jobKind` (ENG-322), via the shared {@link meetsMinimum} so the web and the
 * server agree exactly. A missing/invalid version is `unknown` (non-blocking);
 * a missing minimum (older API) never makes a machine outdated.
 */
export function machineVersionState(
  machine: Partial<Pick<Machine, "cliVersion" | "minMachineVersion">>,
  jobKind: string = MIN_MACHINE_VERSION_DEFAULT_KEY
): MachineVersionState {
  if (!parseMachineVersion(machine.cliVersion)) return "unknown";
  const minimums = machine.minMachineVersion;
  if (!minimums) return "current";
  // Hand the server-resolved map to the shared helper as its override, so it
  // applies the same per-kind → default fallback the server uses.
  const ok = meetsMinimum(machine.cliVersion, jobKind, {
    MIN_MACHINE_VERSION: JSON.stringify(minimums),
  });
  return ok ? "current" : "outdated";
}

/** The minimum CLI version the API reports for `jobKind`, or null if unknown (ENG-322). */
export function machineMinimumVersion(
  machine: Partial<Pick<Machine, "minMachineVersion">>,
  jobKind: string = MIN_MACHINE_VERSION_DEFAULT_KEY
): string | null {
  const minimums = machine.minMachineVersion;
  if (!minimums) return null;
  return minimums[jobKind] ?? minimums[MIN_MACHINE_VERSION_DEFAULT_KEY] ?? null;
}

// =============================================================================
// Remote-aware routing (ENG-178)
// =============================================================================

/**
 * True when `machine` can service the given target git remote (ENG-178) — i.e.
 * the machine has a clone of that project's repo.
 *
 * Both the machine's `servicedRemotes` and the `target` are run through the
 * shared `normalizeGitRemote` so any transport form (scp-like, ssh://,
 * https, trailing `.git`/slash) matches the canonical `https://host/owner/repo`
 * identity the server stores. The comparison is case-insensitive (ENG-353) via
 * {@link canonicalRemoteKey}: GitHub owner/repo names are case-insensitive, so
 * `PaisleyAdmin/CompBox` and `paisleyadmin/compbox` are the same repository.
 *
 * Semantics of `target`:
 *  - a non-empty remote → only machines listing it qualify;
 *  - `null` / `undefined` / `""` → "not remote-scoped": every machine qualifies
 *    (the caller decides whether remote filtering applies at all).
 *
 * A machine whose `servicedRemotes` is absent (an API response predating
 * ENG-176) is treated as `[]` — it services nothing — never dereferenced.
 *
 * Matching ignores letter case: GitHub owner and repo names are
 * case-insensitive, and callers often pass the lowercased `githubRepoKey`.
 */
export function machineServicesRemote(
  machine: Pick<Machine, "servicedRemotes">,
  target: string | null | undefined
): boolean {
  const targetKey = canonicalRemoteKey(target);
  if (targetKey === null) return true;
  return (machine.servicedRemotes ?? []).some((remote) => canonicalRemoteKey(remote) === targetKey);
}

// =============================================================================
// Client-side attachment validation (UX only — API is authoritative)
// =============================================================================

/** Client-side attachment size ceiling, mirroring the server's 2 MB limit. */
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

/**
 * Image MIME types the API stores (mirrors `ALLOWED_IMAGE_MIME_TYPES` in the
 * API's `imageSniff.ts`). SVG is deliberately absent (stored-XSS vector).
 */
export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;

/**
 * Document MIME types the API stores (mirrors `ALLOWED_DOCUMENT_MIME_TYPES`
 * in the API's `attachmentSniff.ts`, ENG-372).
 */
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
] as const;

/** Every MIME type an attachment may be stored as. */
export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
] as const;

/**
 * Filename extensions that are always rejected (lowercase, no leading dot).
 * Mirrors `BLOCKED_ATTACHMENT_EXTENSIONS` in the API's `attachmentSniff.ts` —
 * keep the two lists in sync. The web package can't import from the API.
 */
export const BLOCKED_ATTACHMENT_EXTENSIONS: readonly string[] = [
  // Windows executables / installers / shortcuts
  "exe", "dll", "com", "scr", "msi", "msp", "cpl", "sys", "drv", "pif",
  "lnk", "reg", "hta", "gadget", "application", "appx", "msix",
  // Scripts
  "bat", "cmd", "ps1", "psm1", "psd1", "vbs", "vbe", "js", "jse", "mjs",
  "cjs", "wsf", "wsh", "sh", "bash", "zsh", "csh", "ksh", "fish", "command",
  "py", "pyc", "pyw", "rb", "pl", "php", "phtml", "lua", "applescript",
  "scpt",
  // Unix / macOS / mobile binaries and packages
  "elf", "bin", "run", "so", "dylib", "app", "pkg", "dmg", "deb", "rpm",
  "apk", "ipa", "jar", "war", "class",
  // Office macro-enabled formats
  "docm", "dotm", "xlsm", "xltm", "xlam", "pptm", "potm", "ppam",
  // Browser-active markup
  "html", "htm", "xhtml", "shtml", "svg", "svgz", "xml", "xsl", "swf",
];

const BLOCKED_EXTENSION_SET = new Set(BLOCKED_ATTACHMENT_EXTENSIONS);

/**
 * Allowed extensions. Browsers often report an empty or generic MIME type for
 * `.md` / `.csv` / `.json` files, so the extension is used as a fallback.
 */
const ALLOWED_EXTENSIONS = new Set([
  // images
  "png", "jpg", "jpeg", "gif", "webp",
  // documents
  "pdf", "txt", "text", "log", "csv", "md", "markdown", "json",
]);

/**
 * Value for a file picker's `accept` attribute covering every allowed type
 * (MIME types plus extensions, since some OSes don't map `.md` to a MIME).
 */
export const ATTACHMENT_ACCEPT = [
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
  ...[...ALLOWED_EXTENSIONS].map((ext) => `.${ext}`),
].join(",");

/**
 * Lowercase dotted extension segments of a filename, ignoring any directory
 * part and trailing dots/spaces. Mirrors the API's `extensionSegments`.
 * `"a.tar.gz"` → `["tar", "gz"]`; `"README"` → `[]`.
 */
function extensionSegments(filename: string | null | undefined): string[] {
  if (!filename) return [];
  const base = (filename.split(/[\\/]/).pop() ?? "")
    .trim()
    .replace(/[.\s]+$/, "")
    .toLowerCase();
  return base
    .split(".")
    .slice(1)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/**
 * Return the blocked extension in `filename` (lowercase, no dot), or `null`.
 * Every dotted segment is checked, so `invoice.exe.txt` is blocked too.
 */
export function findBlockedExtension(
  filename: string | null | undefined
): string | null {
  for (const ext of extensionSegments(filename)) {
    if (BLOCKED_EXTENSION_SET.has(ext)) return ext;
  }
  return null;
}

/** True if `mimeType` is one of the image types (as opposed to a document). */
export function isImageMimeType(mimeType: string): boolean {
  return (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Validate an attachment (image or document) before upload. Returns an inline
 * error message when the file is rejected, or `null` when it's acceptable.
 *
 * This is UX only — the API sniffs the content and is the source of truth; the
 * same allowlist, blocklist and size ceiling here just give instant feedback
 * and avoid a doomed round-trip. A file is accepted when it has no blocked
 * extension and either its MIME type or its final extension is allowed.
 */
export function validateAttachment(
  file: Pick<File, "type" | "size"> & { name?: string }
): string | null {
  const blocked = findBlockedExtension(file.name);
  if (blocked) {
    return `.${blocked} files can't be attached.`;
  }
  const mime = (file.type.split(";")[0] ?? "").trim().toLowerCase();
  const segments = extensionSegments(file.name);
  const ext = segments[segments.length - 1];
  const allowedByMime = (
    ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]
  ).includes(mime);
  const allowedByExt = ext !== undefined && ALLOWED_EXTENSIONS.has(ext);
  if (!allowedByMime && !allowedByExt) {
    return "Only images, PDF, text, CSV, Markdown and JSON files can be attached.";
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return isImageMimeType(mime)
      ? "Image is larger than 2 MB."
      : "File is larger than 2 MB.";
  }
  return null;
}

/** Which epic or epic request to look up a running job for. */
export type ActiveJobQuery = { epicId: string } | { epicRequestId: string };

/** The running plan/build for an epic or request (just enough to cancel it). */
export interface ActiveJob {
  id: string;
  status: JobStatus;
  /** When a "waiting" job may run again (ENG-335), or null. */
  resumeAt?: string | null;
  /**
   * Why a "waiting" job is parked (ENG-338): "needs_input" while it waits for
   * the owner's reply, "reply_received" once answered; null for a usage-limit wait.
   */
  waitingReason?: JobWaitingReason | null;
  /** The request a "needs_input" job waits on (ENG-338), or null. */
  needsInput?: NeedsInputRequest | null;
  continuedFromJobId?: string | null;
  continuationIndex?: number;
  maxContinuations?: number;
}

// =============================================================================
// API Functions
// =============================================================================

export const mailboxApi = {
  /** List the current user's paired machines (org-scoped). */
  listMachines: () =>
    api.get<{ data: Machine[] }>("/machines").then((res) => res.data),

  /**
   * Rename a paired machine (ENG-169/ENG-170). PATCHes the machine's display
   * name and returns the updated machine. The API trims and length-checks the
   * name (1–255); a cross-org/non-owned id 404s and an invalid name 400s.
   */
  renameMachine: (id: string, name: string) =>
    api
      .patch<{ data: Machine }>(`/machines/${id}`, { name })
      .then((res) => res.data),

  /**
   * Remove (soft-revoke) a paired machine (ENG-169/ENG-170). The server cuts
   * off the machine's daemon token and fails its non-terminal jobs. Idempotent
   * (re-deleting an already-revoked machine still resolves). Resolves once the
   * revoke is acknowledged; the response body (`{ id, status: 'revoked' }`) is
   * not needed by callers, which re-fetch the (revoked-excluding) machine list.
   */
  /**
   * Turn auto-continuation of usage-limited runs on/off for a machine (ENG-334).
   * Same ownership-scoped PATCH as rename; resolves with the updated machine.
   */
  setMachineAutoContinue: (id: string, autoContinue: boolean) =>
    api
      .patch<{ data: Machine }>(`/machines/${id}`, { autoContinue })
      .then((res) => res.data),

  removeMachine: (id: string) =>
    api.delete<{ data: { id: string; status: string } }>(`/machines/${id}`).then(() => undefined),

  /** Enqueue a job into a machine's mailbox. Returns the created job. */
  enqueueJob: (input: EnqueueJobInput) =>
    api.post<{ data: Job }>("/jobs", input).then((res) => res.data),

  /** Fetch a job with its turns + progress. */
  getJob: (id: string) =>
    api.get<{ data: Job }>(`/jobs/${id}`).then((res) => res.data),

  /**
   * Ask to cancel a job (ENG-299). The server moves a pending job straight to
   * "cancelled" and an in-flight one to "cancelling" until its runner stops.
   * Idempotent; resolves with the updated job. Only the job's owner or an org
   * admin may cancel (403 otherwise).
   */
  cancelJob: (id: string) =>
    api.post<{ data: Job }>(`/jobs/${id}/cancel`, {}).then((res) => res.data),

  /**
   * ER-130: "All merged — continue". The server checks on GitHub that the
   * epic's pull requests are merged (409 PULL_REQUESTS_NOT_MERGED otherwise),
   * then resumes or restarts the build with a note of what was merged.
   */
  continueRun: (id: string, input: { deferredFeatureIds?: string[] } = {}) =>
    api.post<{ data: ContinueRunResult }>(`/jobs/${id}/continue`, input).then((res) => res.data),

  /** ER-130: restart a stopped build on its branch (no merge check). */
  restartRun: (id: string, input: { deferredFeatureIds?: string[] } = {}) =>
    api.post<{ data: ContinueRunResult }>(`/jobs/${id}/restart`, input).then((res) => res.data),

  /**
   * The unfinished plan/build job for an epic or an epic request, or null.
   * Lets those pages offer Cancel without knowing the job id.
   */
  getActiveJob: (query: ActiveJobQuery) => {
    const params = new URLSearchParams(
      "epicId" in query ? { epicId: query.epicId } : { epicRequestId: query.epicRequestId },
    );
    return api
      .get<{ data: ActiveJob | null }>(`/jobs/active?${params.toString()}`)
      .then((res) => res.data);
  },

  /** Append a user turn to a job (answer the interview). */
  postTurn: (jobId: string, input: PostTurnInput) =>
    api
      .post<{ data: Job }>(`/jobs/${jobId}/turns`, input)
      .then((res) => res.data),

  /**
   * Send a follow-up to a finished build (ENG-363/ENG-364). The server creates
   * a new pending build job on the same machine, linked to the parent via
   * `continuedFromJobId`, with the message as its first turn — and returns it.
   * Errors (403 not owner · 409 not finished / follow-up already running /
   * chain at its cap · 422 secret in message) come back as an `ApiError`.
   */
  createFollowUp: (jobId: string, input: PostTurnInput) =>
    api
      .post<{ data: Job }>(`/jobs/${jobId}/follow-ups`, input)
      .then((res) => res.data),

  /**
   * Upload an image file to a job's attachment store (ENG-151).
   *
   * Sends `multipart/form-data` with the file under the `file` field. We call
   * `api.request` directly (not `api.post`, which JSON-stringifies its body) so
   * the `FormData` is sent verbatim — the ApiClient deliberately leaves the
   * `Content-Type` unset for `FormData` bodies so the browser can attach the
   * multipart boundary. The server enforces the 2 MB + image-MIME limits and
   * returns the stored attachment (short-TTL `signedUrl` included).
   */
  uploadAttachment: (jobId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .request<{ data: JobAttachment }>(`/jobs/${jobId}/attachments`, {
        method: "POST",
        body: form,
      })
      .then((res) => res.data);
  },

  /**
   * Ingest an image by URL into a job's attachment store (ENG-151) — the JSON
   * `{ url }` variant of the upload endpoint. The server fetches, validates
   * (2 MB + image-MIME), and stores it, returning the stored attachment.
   */
  uploadAttachmentUrl: (jobId: string, url: string) =>
    api
      .post<{ data: JobAttachment }>(`/jobs/${jobId}/attachments`, { url })
      .then((res) => res.data),
};
